import { useState, useMemo } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Heart, ShieldCheck, Loader2 } from "lucide-react";

const PRESET_AMOUNTS = [10, 25, 50, 100, 250];
const TIP_PRESETS = [
  { pct: 0, label: "None" },
  { pct: 5, label: "5%" },
  { pct: 10, label: "10%" },
  { pct: 15, label: "15%" },
];

/**
 * DonateDialog — public donation flow (no auth required).
 *
 * Props:
 *   - open, onOpenChange
 *   - nonprofitUserId (required)
 *   - orgName (display only)
 *   - eventId (optional — attaches donation to a fundraiser event)
 */
export default function DonateDialog({
  open, onOpenChange, nonprofitUserId, orgName, eventId,
}) {
  const [amountDollars, setAmountDollars] = useState(25);
  const [customAmount, setCustomAmount] = useState("");
  const [tipPct, setTipPct] = useState(10);
  const [customTip, setCustomTip] = useState("");
  const [donorName, setDonorName] = useState("");
  const [donorEmail, setDonorEmail] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const donationCents = useMemo(() => {
    const val = customAmount !== "" ? parseFloat(customAmount) : amountDollars;
    if (isNaN(val) || val < 1) return 0;
    return Math.round(val * 100);
  }, [amountDollars, customAmount]);

  const tipCents = useMemo(() => {
    if (customTip !== "") {
      const c = parseFloat(customTip);
      return isNaN(c) ? 0 : Math.max(0, Math.round(c * 100));
    }
    return Math.round((donationCents * tipPct) / 100);
  }, [donationCents, tipPct, customTip]);

  const totalCents = donationCents + tipCents;

  const submit = async () => {
    if (!donorName.trim()) return toast.error("Please add your name for the receipt.");
    if (!donorEmail.trim() || !donorEmail.includes("@")) {
      return toast.error("Please enter a valid email for your tax receipt.");
    }
    if (donationCents < 100) return toast.error("Donation must be at least $1.");
    setSubmitting(true);
    try {
      const { data } = await api.post("/donations/checkout", {
        nonprofit_user_id: nonprofitUserId,
        amount_cents: donationCents,
        tip_cents: tipCents,
        donor_name: donorName.trim(),
        donor_email: donorEmail.trim(),
        message: message.trim(),
        event_id: eventId || null,
        origin_url: window.location.origin,
      });
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      toast.error("Couldn't start checkout. Please try again.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Donation failed to start");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="donate-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl flex items-center gap-2">
            <Heart className="h-5 w-5" style={{ color: "var(--avt-pink)" }} fill="var(--avt-pink)" strokeWidth={0} />
            Support {orgName || "this nonprofit"}
          </DialogTitle>
          <DialogDescription className="text-sm">
            100% of your donation goes to the nonprofit &mdash; AddVendTour takes 0%.
            You&apos;ll get a tax receipt at your email.
          </DialogDescription>
        </DialogHeader>

        {/* Amount presets */}
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-widest" style={{ color: "var(--ink-soft)" }}>
            Donation amount
          </Label>
          <div className="grid grid-cols-5 gap-2" data-testid="donate-preset-amounts">
            {PRESET_AMOUNTS.map((amt) => (
              <button
                key={amt}
                data-testid={`donate-preset-${amt}`}
                onClick={() => { setAmountDollars(amt); setCustomAmount(""); }}
                className="py-2 rounded-lg border text-sm transition-all"
                style={{
                  backgroundColor: (customAmount === "" && amountDollars === amt)
                    ? "var(--avt-purple)" : "var(--sand)",
                  color: (customAmount === "" && amountDollars === amt) ? "#fff" : "var(--ink)",
                  borderColor: (customAmount === "" && amountDollars === amt)
                    ? "var(--avt-purple)" : "var(--border-warm)",
                }}
              >
                ${amt}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 pt-1">
            <span className="text-sm" style={{ color: "var(--ink-soft)" }}>Or:</span>
            <div className="relative flex-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm" style={{ color: "var(--ink-soft)" }}>$</span>
              <Input
                data-testid="donate-custom-amount"
                type="number"
                min="1"
                step="1"
                placeholder="Custom amount"
                value={customAmount}
                onChange={(e) => setCustomAmount(e.target.value)}
                className="pl-7"
              />
            </div>
          </div>
        </div>

        {/* Tip */}
        <div className="space-y-2 pt-2">
          <Label className="text-xs uppercase tracking-widest" style={{ color: "var(--ink-soft)" }}>
            Optional tip to AddVendTour
          </Label>
          <p className="text-xs" style={{ color: "var(--ink-soft)" }}>
            Keeps this platform running so nonprofits like {orgName || "this one"} can raise more.
            Not tax-deductible.
          </p>
          <div className="grid grid-cols-4 gap-2" data-testid="donate-tip-presets">
            {TIP_PRESETS.map(({ pct, label }) => (
              <button
                key={pct}
                data-testid={`donate-tip-${pct}`}
                onClick={() => { setTipPct(pct); setCustomTip(""); }}
                className="py-2 rounded-lg border text-sm transition-all"
                style={{
                  backgroundColor: (customTip === "" && tipPct === pct)
                    ? "var(--avt-teal)" : "var(--sand)",
                  color: (customTip === "" && tipPct === pct) ? "#fff" : "var(--ink)",
                  borderColor: (customTip === "" && tipPct === pct)
                    ? "var(--avt-teal)" : "var(--border-warm)",
                }}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 pt-1">
            <span className="text-sm" style={{ color: "var(--ink-soft)" }}>Custom tip:</span>
            <div className="relative w-32">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm" style={{ color: "var(--ink-soft)" }}>$</span>
              <Input
                data-testid="donate-custom-tip"
                type="number"
                min="0"
                step="0.50"
                placeholder="0.00"
                value={customTip}
                onChange={(e) => setCustomTip(e.target.value)}
                className="pl-7"
              />
            </div>
          </div>
        </div>

        {/* Donor info */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
          <div>
            <Label className="text-xs uppercase tracking-widest" style={{ color: "var(--ink-soft)" }}>Your name</Label>
            <Input
              data-testid="donate-name"
              value={donorName}
              onChange={(e) => setDonorName(e.target.value)}
              placeholder="Jane Doe"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest" style={{ color: "var(--ink-soft)" }}>Email (for receipt)</Label>
            <Input
              data-testid="donate-email"
              type="email"
              value={donorEmail}
              onChange={(e) => setDonorEmail(e.target.value)}
              placeholder="jane@example.com"
            />
          </div>
        </div>
        <div className="pt-1">
          <Label className="text-xs uppercase tracking-widest" style={{ color: "var(--ink-soft)" }}>Message (optional)</Label>
          <Textarea
            data-testid="donate-message"
            rows={2}
            maxLength={500}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Keep up the amazing work…"
          />
        </div>

        {/* Total + submit */}
        <div className="pt-3 border-t space-y-3" style={{ borderColor: "var(--border-warm)" }}>
          <div className="flex items-center justify-between text-sm">
            <span style={{ color: "var(--ink-soft)" }}>To {orgName || "the nonprofit"}</span>
            <span className="font-semibold" data-testid="donate-donation-total">${(donationCents / 100).toFixed(2)}</span>
          </div>
          {tipCents > 0 && (
            <div className="flex items-center justify-between text-xs" style={{ color: "var(--ink-soft)" }}>
              <span>Tip to AddVendTour</span>
              <span data-testid="donate-tip-total">${(tipCents / 100).toFixed(2)}</span>
            </div>
          )}
          <div className="flex items-center justify-between text-lg font-serif">
            <span>You&apos;ll be charged</span>
            <span data-testid="donate-grand-total" style={{ color: "var(--avt-purple)" }}>
              ${(totalCents / 100).toFixed(2)}
            </span>
          </div>
          <Button
            data-testid="donate-submit"
            onClick={submit}
            disabled={submitting || donationCents < 100}
            className="w-full btn-primary rounded-full py-6 text-base"
          >
            {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Redirecting to Stripe…</> :
              <><Heart className="h-4 w-4 mr-2" /> Donate ${(totalCents / 100).toFixed(2)}</>}
          </Button>
          <p className="text-[11px] text-center flex items-center justify-center gap-1" style={{ color: "var(--ink-soft)" }}>
            <ShieldCheck className="h-3 w-3" /> Secured by Stripe. Tax-deductible receipt emailed instantly.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
